import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseAlertBanner, BaseButton } from '@components';
import ClientLogo from '@components/logo';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import { REPEAT_PASSWORD_FIELD_NAME } from '@constants/auth.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import type { NavigateFn } from '@hooks/useCustomNavigate.ts';
import { useCustomNavigate } from '@hooks/useCustomNavigate.ts';
import FooterLogo from '@layouts/auth/footer-logo';
import { useLingui } from '@lingui/react/macro';
import { ROUTES } from '@router/constants.ts';
import {
    arePasswordsMismatched,
    getPasswordDefaultHint,
    getPasswordPolicyHint,
    getUpdatedFormFieldsWithErrors,
    showErrorForNewPasswordField,
    validatePasswordChange,
} from '@routes/auth/___shared___/helpers.ts';
import { renderChangePasswordInput } from '@routes/auth/___shared___/utils.tsx';
import { setUserNewPasswordFirstTime } from '@routes/auth/register/model/register.slice.ts';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { getRegistrationUserEmail } from '@store/services/localStorage.service.ts';
import type { FormField } from '@types';
import { renderAlertErrorDesc } from '@utils/general.tsx';
import { Form } from 'antd';

import { AuthSharedWrapper, FooterLogoSection, FormSection } from '../___shared___/styled.tsx';
import { AlertSection } from './styled.tsx';

const verifyReferrerAndRedirectIfInvalid = (navigate: NavigateFn, referrer: string) => {
    if (referrer !== ROUTES.AUTH.REGISTRATION) return navigate(ROUTES.AUTH.LOGIN);
};

const NETWORK_ERROR_RESPONSE = 'Network Error';
const OLD_PASSWORD_FIELD_NAME = 'InitialPassword';
const NEW_PASSWORD_FIELD_NAME = 'Password';
const SAME_AS_INITIAL_PASSWORD_ERROR_MSG = 'Same as InitialPassword';
const SAME_AS_PASSWORD_ERROR_MSG = 'Same as Password';

const PasswordChange = () => {
    const { t } = useLingui();
    const [form] = Form.useForm();
    const ALERT_BANNER_MSG = t`You must change your password when logging in for the first time.`;
    const PASSWORDS_DO_NOT_MATCH_ERROR_MSG = t`Passwords do not match. Please re-enter the same password in both fields.`;
    const PASSWORD_REQUIRED_MSG = t`Password cannot be empty.`;
    const OLD_PASSWORD_INVALID_MSG = t`The current password you entered is incorrect`;
    const NETWORK_ERROR_MSG = t`Network error occurred.`;

    const [isPasswordType, setIsPasswordType] = useState({ old: true, new: true, repeat: true });
    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [newPasswordHintText, setNewPasswordHintText] = useState('');

    const navigate = useCustomNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const fromPath = location.state?.from;
    const currentUserPassword = location.state?.tempPassword;

    const email = useAppSelector((state) => state.authRegister.email || getRegistrationUserEmail());
    const userType = useAppSelector((state) => state.authRegister.userType);

    const clientStatus = useAppSelector((state) => state.clientStatus.clientStatus);
    const passwordPoliciesPerType = clientStatus?.passwordPolicyPerType.find((pt) => pt.userType === userType);

    useEffect(() => {
        if (passwordPoliciesPerType) {
            // wait to get client status to set default policies
            setNewPasswordHintText(getPasswordDefaultHint(passwordPoliciesPerType.passwordPolicies));
        }
    }, [clientStatus, passwordPoliciesPerType]);

    const onFieldsChange = (_: any[], allFields: any[]) => setFormFields(allFields as FormField[]);

    useEffect(() => verifyReferrerAndRedirectIfInvalid(navigate, fromPath), [fromPath, navigate]);

    const handleNetworkError = () => {
        const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, [
            {
                name: OLD_PASSWORD_FIELD_NAME,
                message: NETWORK_ERROR_MSG,
            },
        ]);
        setFormFields(updatedFields);
    };

    const handlePasswordErrors = (errors: { error: string; name: string }[] = []) => {
        if (errors.length === 0) return;

        const fieldErrors: { name: string; message: string }[] = [];
        const mappedResponseErrors = errors.map((error) => ({ name: error.name, message: error.error }));

        if (mappedResponseErrors.length > 0) {
            fieldErrors.push(...mappedResponseErrors);
        }

        if (errors.some((e) => e.error === SAME_AS_INITIAL_PASSWORD_ERROR_MSG)) {
            fieldErrors.push({
                name: NEW_PASSWORD_FIELD_NAME,
                message: t`New password cannot be the same as Old password`,
            });
        }

        if (errors.some((e) => e.error === SAME_AS_PASSWORD_ERROR_MSG)) {
            fieldErrors.push({
                name: OLD_PASSWORD_FIELD_NAME,
                message: t`Old password cannot be the same as New Password`,
            });
        }

        if (fieldErrors.length) {
            const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, fieldErrors);
            setFormFields(updatedFields);
        }
    };

    const handleSubmit = () => {
        form.validateFields().then(({ Password, InitialPassword, repeatPassword }) => {
            const { canProceed, hint } = validatePasswordChange({
                policies: passwordPoliciesPerType?.passwordPolicies,
                newPassword: Password,
                repeatPassword,
                getNextPolicyHint: getPasswordPolicyHint,
            });

            if (!canProceed) {
                showErrorForNewPasswordField(formFields, setFormFields, hint, NEW_PASSWORD_FIELD_NAME);

                return; // abort submit if some policy is not fulfilled or passwords don't match
            }

            if (InitialPassword !== currentUserPassword) {
                const { updatedFields } = getUpdatedFormFieldsWithErrors(formFields, [
                    { name: OLD_PASSWORD_FIELD_NAME, message: OLD_PASSWORD_INVALID_MSG },
                ]);
                setFormFields(updatedFields);
                return;
            }

            setIsLoading(true);
            dispatch(
                setUserNewPasswordFirstTime({
                    email: email,
                    password: Password,
                    initialPassword: InitialPassword,
                }),
            )
                .then(() => {
                    setIsLoading(false);
                    navigate(ROUTES.AUTH.RESET_PASSWORD_SUCCESS);
                })
                .catch((err) => {
                    setIsLoading(false);
                    console.error(err);

                    const isNetworkError = typeof err === 'string' && err === NETWORK_ERROR_RESPONSE;

                    if (isNetworkError) {
                        handleNetworkError();
                    } else {
                        handlePasswordErrors(err?.data?.errorFields || []);
                    }

                    if (err.status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
                        dispatch(clearAlert()); //skip banner
                    } else {
                        dispatch(
                            setAlert({
                                type: ALERT_STATUSES.ERROR,
                                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                                description: renderAlertErrorDesc(err.status, err.statusText),
                            }),
                        );
                    }
                });
        });
    };

    return (
        <AuthSharedWrapper>
            <FormSection>
                <ClientLogo />

                <AlertSection>
                    <BaseAlertBanner description={ALERT_BANNER_MSG} type="info" />
                </AlertSection>

                <Form form={form} onFinish={handleSubmit} onFieldsChange={onFieldsChange}>
                    {renderChangePasswordInput(
                        PASSWORD_REQUIRED_MSG,
                        OLD_PASSWORD_FIELD_NAME,
                        'old',
                        t`Old password`,
                        t`Enter your old password`,
                        formFields,
                        isPasswordType,
                        setIsPasswordType,
                    )}

                    {renderChangePasswordInput(
                        PASSWORD_REQUIRED_MSG,
                        NEW_PASSWORD_FIELD_NAME,
                        'new',
                        t`New password`,
                        t`Enter new password`,
                        formFields,
                        isPasswordType,
                        setIsPasswordType,
                        newPasswordHintText,
                    )}

                    {renderChangePasswordInput(
                        PASSWORD_REQUIRED_MSG,
                        REPEAT_PASSWORD_FIELD_NAME,
                        'repeat',
                        t`Repeat new password`,
                        t`Enter new password`,
                        formFields,
                        isPasswordType,
                        setIsPasswordType,
                        undefined,
                        true,
                        PASSWORDS_DO_NOT_MATCH_ERROR_MSG,
                        () => arePasswordsMismatched(formFields, NEW_PASSWORD_FIELD_NAME),
                    )}

                    <Form.Item noStyle>
                        <BaseButton
                            className="full-width"
                            loading={isLoading}
                            htmlType="submit"
                            data-cy="passw-change-button"
                        >
                            {t`Save & Login`}
                        </BaseButton>
                    </Form.Item>
                </Form>
            </FormSection>

            <FooterLogoSection>
                <FooterLogo />
            </FooterLogoSection>
        </AuthSharedWrapper>
    );
};

export default PasswordChange;
